/**
 * StintSense — Data Formatters
 */

/** Format seconds to mm:ss.xxx lap time string */
export function formatLapTime(seconds: number | null | undefined): string {
  if (seconds == null || seconds <= 0) return "--:--.---";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const whole = Math.floor(secs);
  const ms = Math.round((secs - whole) * 1000);
  return `${mins}:${String(whole).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

/** Format delta in seconds to +/-x.xxx string */
export function formatDelta(seconds: number | null | undefined): string {
  if (seconds == null) return "-";
  const sign = seconds >= 0 ? "+" : "-";
  const abs = Math.abs(seconds);
  return `${sign}${abs.toFixed(3)}`;
}

/** Format temperature with unit */
export function formatTemp(
  value: number | null | undefined,
  unit: string = "F"
): string {
  if (value == null) return "N/A";
  return `${value.toFixed(1)}°${unit}`;
}

/** Format pressure with unit */
export function formatPressure(
  value: number | null | undefined,
  unit: string = "PSI"
): string {
  if (value == null) return "N/A";
  return `${value.toFixed(1)} ${unit}`;
}

/** Format wear percentage */
export function formatWear(value: number | null | undefined): string {
  if (value == null) return "N/A";
  return `${value.toFixed(1)}%`;
}

/** Format falloff s/lap */
export function formatFalloff(sPerLap: number | null | undefined): string {
  if (sPerLap == null) return "N/A";
  const sign = sPerLap >= 0 ? "+" : "";
  return `${sign}${sPerLap.toFixed(3)} s/lap`;
}

const LITERS_PER_GALLON = 3.785411784;

/** Convert stored liters-based fuel values into the current display unit system. */
export function fuelValueForDisplay(
  liters: number | null | undefined,
  units: string | null | undefined
): number | null {
  if (liters == null) return null;
  return units === "metric" ? liters : liters / LITERS_PER_GALLON;
}

/** Resolve the current fuel unit label. */
export function fuelUnitForDisplay(units: string | null | undefined): "L" | "gal" {
  return units === "metric" ? "L" : "gal";
}

/** Format a stored liters-based fuel-per-lap value for UI display. */
export function formatFuelPerLap(
  liters: number | null | undefined,
  units: string | null | undefined,
  decimals = 2
): string {
  const value = fuelValueForDisplay(liters, units);
  if (value == null) return "N/A";
  return `${value.toFixed(decimals)} ${fuelUnitForDisplay(units)}/lap`;
}

/** Format a stored liters-based fuel amount for UI display. */
export function formatFuelAmount(
  liters: number | null | undefined,
  units: string | null | undefined,
  decimals = 3
): string {
  const value = fuelValueForDisplay(liters, units);
  if (value == null) return "N/A";
  return `${value.toFixed(decimals)} ${fuelUnitForDisplay(units)}`;
}

/** Format an ISO timestamp into a compact recency label. */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "N/A";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);

  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Parse an iRacing setup file path into a clean display object.
 *  Input:  "Outlier 2026\\Coke Qualifying\\Rockingham\\OSC_2651_Coke-Qual_Rockingham_Race_V0.5.4"
 *  Output: { name: "OSC 2651 Coke-Qual Rockingham Race", version: "V0.5.4", display: "OSC 2651 Coke-Qual Rockingham Race · V0.5.4" }
 */
export function parseSetupName(raw: string | null | undefined): {
  name: string;
  version: string | null;
  display: string;
} {
  if (!raw) return { name: "Unnamed", version: null, display: "Unnamed" };
  const normalized = raw.replace(/\\/g, "/");
  const parts = normalized.split("/").filter(Boolean);
  const leaf = (parts[parts.length - 1] || raw).replace(/\.sto$/i, "");

  const versionMatch = leaf.match(/[_\s-](V?\d+(?:\.\d+){1,3}[a-z]?)$/i);
  const version = versionMatch ? versionMatch[1] : null;
  const baseName = version ? leaf.slice(0, versionMatch!.index).replace(/[_\s-]+$/, "") : leaf;
  const name = baseName.replace(/_/g, " ").trim() || leaf;

  return {
    name,
    version,
    display: version ? `${name}  ·  ${version}` : name,
  };
}

/** Quick one-shot: get just the clean display string. */
export function formatSetupName(raw: string | null | undefined): string {
  return parseSetupName(raw).display;
}

/** Turn backend session types into cleaner UI labels. */
export function formatSessionType(value: string | null | undefined): string {
  if (!value) return "Session";
  const normalized = value.trim().toLowerCase();
  if (normalized === "practice") return "Practice";
  if (normalized === "qualify" || normalized === "qualifying") return "Qualifying";
  if (normalized === "race") return "Race";
  if (normalized === "warmup") return "Warmup";
  return value;
}

/** Detect whether a run is explicitly qualifying-focused. */
export function isQualifyingRun(input: {
  eventType?: string | null;
  setupName?: string | null;
  runStatus?: string | null;
}): boolean {
  const eventType = (input.eventType || "").toLowerCase();
  const setupName = (input.setupName || "").toLowerCase();
  const runStatus = (input.runStatus || "").toLowerCase();
  if (runStatus === "approved_quali") return true;
  return ["qualifying", "quali", "qualify"].some(
    (marker) => eventType.includes(marker) || setupName.includes(marker)
  );
}

/** Strip season suffixes from iRacing series labels while preserving Open/Fixed. */
export function formatSeriesDisplayName(value: string | null | undefined): string {
  if (!value) return "Uncategorized";

  const raw = value.replace(/[–—]/g, " - ").replace(/\s+/g, " ").trim();
  if (!raw) return "Uncategorized";

  const hasFixed = /\bfixed\b/i.test(raw);
  const hasOpen = !hasFixed && /\bopen\b/i.test(raw);

  let cleaned = raw
    .replace(/\b20\d{2}\s+season(?:\s+\d+)?\b/gi, " ")
    .replace(/\bseason\s+\d+\b/gi, " ")
    .replace(/\b\d{2}s\d\b/gi, " ")
    .replace(/\b(open|fixed)\b/gi, " ")
    .replace(/\s+-\s+/g, " ")
    .replace(/(?:^|\s)-(?=\s|$)/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) {
    cleaned = raw;
  }

  if (hasFixed && !/\bfixed\b/i.test(cleaned)) {
    return `${cleaned} Fixed`.trim();
  }

  if (hasOpen && !/\bopen\b/i.test(cleaned)) {
    return `${cleaned} Open`.trim();
  }

  return cleaned;
}
